import { Product } from '@components/ui/Product';
import { formatLineBatchDate } from '@shared/utils/formatLineBatchDate';
import React from 'react';
import type { LinePick, PreparationLine } from '../../models/PreparationModels';
import { sanitizeBatchNumberForDisplay } from '@shared/utils/sanitizeBatchDisplay';

const STYLES = {
  qtyColor: 'text-green-600 dark:text-green-400',
  barBg: 'bg-green-200 dark:bg-green-800',
  barFill: 'bg-green-500',
};

interface LinePickCompletedCardProps {
  pick: LinePick;
  /** Línea de preparación con `id === pick.stock_location_order_line_id` */
  line?: PreparationLine;
}

/**
 * Mismo formato que `ProductLineItem` (variante completada), con badge de lote alineado a PackingV2 (`bg-muted/60` + `border-border`).
 */
export const LinePickCompletedCard: React.FC<LinePickCompletedCardProps> = ({ pick, line }) => {
  const p = line?.product;
  const lineQty = line?.qty ?? pick.qty;
  const progress = lineQty > 0 ? Math.min((pick.qty / lineQty) * 100, 100) : 100;
  const batch = pick.batch;
  const lotLabel = sanitizeBatchNumberForDisplay(batch?.batch_number);
  const hasExpiry = !!batch?.expiry_date?.toString().trim();
  const showLotBadge = !!(lotLabel || hasExpiry);

  return (
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <Product
          id={pick.product_id}
          src={p?.image || ''}
          name={p?.name || `Producto ID: ${pick.product_id}`}
          code={p?.code}
          acronym={p?.name?.charAt(0).toUpperCase()}
          showImageModal={true}
          enableLineClamp={true}
        />
        {showLotBadge ? (
          <div className="mt-2 space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Lote pickeado</p>
            <div className="text-xs bg-muted/60 border border-border rounded-md px-2 py-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              {lotLabel ? <span className="font-medium text-foreground">{lotLabel}</span> : null}
              {batch?.expiry_date ? (
                <span className="text-muted-foreground">Cad. {formatLineBatchDate(batch.expiry_date)}</span>
              ) : null}
              <span className="text-muted-foreground ml-auto sm:ml-0">{pick.qty} uds. pickeadas</span>
            </div>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col items-end space-y-1 ml-4">
        <div className="flex items-center space-x-2">
          <span className={`text-lg font-semibold ${STYLES.qtyColor}`}>{pick.qty}</span>
          <span className="text-muted-foreground">/</span>
          <span className="text-lg font-semibold text-muted-foreground">{lineQty}</span>
        </div>

        <div className={`w-16 h-2 ${STYLES.barBg} rounded-full overflow-hidden`}>
          <div className={`h-full ${STYLES.barFill}`} style={{ width: `${progress}%` }} />
        </div>
      </div>
    </div>
  );
};
